import React from 'react';
import { Card, Table, Tag } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';

interface RowStat {
    original_index: number;
    miss_count: number;
    hit_count: number;
    hit_rate: number;
    missing_columns: number[];
}

interface ReorderMapping {
    old_to_new: number[];
    new_to_old: number[];
}

interface MatrixReorderProcessProps {
    rowStats: RowStat[];
    reorderMapping: ReorderMapping;
    partitionBoundary: number;
    n: number;
    missingPairs: number[][];
}

const MatrixReorderProcess: React.FC<MatrixReorderProcessProps> = ({
    rowStats,
    reorderMapping,
    partitionBoundary,
    n,
    missingPairs
}) => {
    // 生成数字标签（从1开始）
    const getLabel = (index: number): string => {
        return String(index + 1); // 1, 2, 3, ...
    };

    const getHitRateColor = (rate: number) => {
        if (rate >= 0.75) return 'success';
        if (rate >= 0.5) return 'warning';
        return 'error';
    };

    const getHitRateEmoji = (rate: number) => {
        if (rate >= 0.75) return '🟢';
        if (rate >= 0.5) return '🟡';
        return '🔴';
    };

    // 构建缺失集合（原始顺序）
    const missingSet = new Set(
        missingPairs.map(([row, col]) => `${row},${col}`)
    );

    // 检查原始矩阵中是否缺失
    const isMissingOriginal = (row: number, col: number): boolean => {
        return row !== col && missingSet.has(`${row},${col}`);
    };

    // 检查重排后矩阵中是否缺失
    const isMissingReordered = (newRow: number, newCol: number): boolean => {
        if (newRow === newCol) return false;
        const oldRow = reorderMapping.new_to_old[newRow];
        const oldCol = reorderMapping.new_to_old[newCol];
        return missingSet.has(`${oldRow},${oldCol}`);
    };

    // 渲染小矩阵
    const renderMiniMatrix = (
        startRow: number,
        endRow: number,
        startCol: number,
        endCol: number,
        title: string,
        bgColor: string
    ) => {
        const cellSize = n <= 10 ? 28 : 20;
        const fontSize = n <= 10 ? '12px' : '10px';

        return (
            <div className="inline-block">
                <div className="text-sm font-semibold mb-2 text-center">{title}</div>
                <div style={{ backgroundColor: bgColor }} className="inline-block p-2 rounded">
                    {/* 列标签 */}
                    <div className="flex">
                        <div style={{ width: cellSize, height: cellSize, minWidth: cellSize }} />
                        {Array.from({ length: endCol - startCol }).map((_, i) => {
                            const oldColIndex = reorderMapping.new_to_old[startCol + i];
                            return (
                                <div
                                    key={`col-${i}`}
                                    className="flex items-center justify-center font-bold text-gray-700"
                                    style={{ width: cellSize, height: cellSize, minWidth: cellSize, fontSize }}
                                >
                                    {getLabel(oldColIndex)}
                                </div>
                            );
                        })}
                    </div>

                    {/* 矩阵内容 */}
                    {Array.from({ length: endRow - startRow }).map((_, i) => {
                        const newRowIndex = startRow + i;
                        const oldRowIndex = reorderMapping.new_to_old[newRowIndex];
                        return (
                            <div key={`row-${i}`} className="flex">
                                {/* 行标签 */}
                                <div
                                    className="flex items-center justify-center font-bold text-gray-700"
                                    style={{ width: cellSize, height: cellSize, minWidth: cellSize, fontSize }}
                                >
                                    {getLabel(oldRowIndex)}
                                </div>

                                {/* 单元格 */}
                                {Array.from({ length: endCol - startCol }).map((_, j) => {
                                    const newColIndex = startCol + j;
                                    const isDiagonal = newRowIndex === newColIndex;
                                    const missing = isMissingReordered(newRowIndex, newColIndex);

                                    return (
                                        <div
                                            key={`cell-${i}-${j}`}
                                            className="flex items-center justify-center border border-gray-200"
                                            style={{
                                                width: cellSize,
                                                height: cellSize,
                                                minWidth: cellSize,
                                                backgroundColor: isDiagonal
                                                    ? '#f0f0f0'
                                                    : missing
                                                    ? '#fff1f0'
                                                    : '#f6ffed',
                                                fontSize
                                            }}
                                        >
                                            {isDiagonal ? (
                                                <span className="text-gray-400">-</span>
                                            ) : missing ? (
                                                <CloseOutlined style={{ color: '#ff4d4f', fontSize: '10px' }} />
                                            ) : (
                                                <CheckOutlined style={{ color: '#52c41a', fontSize: '10px' }} />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    // 表格列定义
    const columns = [
        {
            title: '原始行',
            dataIndex: 'original_index',
            key: 'original_index',
            render: (index: number) => <strong>{getLabel(index)}</strong>
        },
        {
            title: '缺失数',
            dataIndex: 'miss_count',
            key: 'miss_count',
            render: (count: number) => (
                <span className="text-red-500 font-semibold">{count}个 ❌</span>
            ),
            sorter: (a: RowStat, b: RowStat) => b.miss_count - a.miss_count
        },
        {
            title: '命中率',
            dataIndex: 'hit_rate',
            key: 'hit_rate',
            render: (rate: number) => (
                <span>
                    <Tag color={getHitRateColor(rate)}>
                        {(rate * 100).toFixed(1)}%
                    </Tag>
                    {getHitRateEmoji(rate)}
                </span>
            )
        },
        {
            title: '缺失列',
            dataIndex: 'missing_columns',
            key: 'missing_columns',
            render: (cols: number[]) => (
                <span className="text-gray-600">
                    [{cols.map(c => getLabel(c)).join(', ')}]
                </span>
            )
        }
    ];

    return (
        <Card title="🔄 矩阵重排过程 (策略3专属)">
            <div className="space-y-6">
                {/* 步骤1: 统计每行缺失 */}
                <div>
                    <h4 className="text-lg font-semibold mb-3">📋 步骤1: 统计每行缺失</h4>
                    <Table
                        dataSource={rowStats}
                        columns={columns}
                        pagination={false}
                        size="small"
                        rowKey="original_index"
                    />
                </div>

                {/* 步骤2: 按缺失数排序 */}
                <div>
                    <h4 className="text-lg font-semibold mb-3">🔀 步骤2: 按缺失数排序</h4>
                    <div className="bg-gray-50 p-4 rounded">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <div className="font-semibold mb-2">原顺序 → 新位置</div>
                                <div className="space-y-1 text-sm">
                                    {reorderMapping.new_to_old.map((oldIndex, newIndex) => {
                                        const stat = rowStats.find(s => s.original_index === oldIndex);
                                        return (
                                            <div key={newIndex} className="flex items-center gap-2">
                                                <span className="font-mono">
                                                    {getLabel(oldIndex)}({stat?.miss_count}个)
                                                </span>
                                                <span>→</span>
                                                <span className="text-blue-600 font-semibold">
                                                    新位置 {newIndex}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 步骤2.5: 重排后矩阵 */}
                <div>
                    <h4 className="text-lg font-semibold mb-3">📊 重排后矩阵</h4>
                    <div className="bg-gray-50 p-4 rounded overflow-auto">
                        <div className="inline-block">
                            <div className="text-sm text-gray-600 mb-2">
                                按缺失数重新排列后的矩阵（行和列使用原始标签）
                            </div>
                            <div className="inline-block">
                                {/* 列标签 */}
                                <div className="flex">
                                    <div style={{ width: 32, height: 32, minWidth: 32 }} />
                                    {Array.from({ length: n }).map((_, newColIndex) => {
                                        const oldColIndex = reorderMapping.new_to_old[newColIndex];
                                        return (
                                            <div
                                                key={`col-${newColIndex}`}
                                                className="flex items-center justify-center font-bold text-gray-700"
                                                style={{ width: 32, height: 32, minWidth: 32, fontSize: '12px' }}
                                            >
                                                {getLabel(oldColIndex)}
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* 矩阵内容 */}
                                {Array.from({ length: n }).map((_, newRowIndex) => {
                                    const oldRowIndex = reorderMapping.new_to_old[newRowIndex];
                                    return (
                                        <div key={`row-${newRowIndex}`} className="flex">
                                            {/* 行标签 */}
                                            <div
                                                className="flex items-center justify-center font-bold text-gray-700"
                                                style={{ width: 32, height: 32, minWidth: 32, fontSize: '12px' }}
                                            >
                                                {getLabel(oldRowIndex)}
                                            </div>

                                            {/* 单元格 */}
                                            {Array.from({ length: n }).map((_, newColIndex) => {
                                                const isDiagonal = newRowIndex === newColIndex;
                                                const missing = isMissingReordered(newRowIndex, newColIndex);

                                                return (
                                                    <div
                                                        key={`cell-${newRowIndex}-${newColIndex}`}
                                                        className="flex items-center justify-center border border-gray-200"
                                                        style={{
                                                            width: 32,
                                                            height: 32,
                                                            minWidth: 32,
                                                            backgroundColor: isDiagonal
                                                                ? '#f0f0f0'
                                                                : missing
                                                                ? '#fff1f0'
                                                                : '#f6ffed',
                                                            fontSize: '12px'
                                                        }}
                                                    >
                                                        {isDiagonal ? (
                                                            <span className="text-gray-400">-</span>
                                                        ) : missing ? (
                                                            <CloseOutlined style={{ color: '#ff4d4f', fontSize: '10px' }} />
                                                        ) : (
                                                            <CheckOutlined style={{ color: '#52c41a', fontSize: '10px' }} />
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>

                {/* 步骤3: 分区识别 */}
                <div>
                    <h4 className="text-lg font-semibold mb-3">📐 步骤3: 分区识别 (阈值: 70%)</h4>
                    <div className="bg-gray-50 p-4 rounded space-y-4">
                        <div>
                            <span className="font-semibold">分界点: </span>
                            <span>第 {partitionBoundary} 行</span>
                            {partitionBoundary < n && (
                                <span className="text-gray-600 ml-2">
                                    (命中率 {(rowStats.find(s => 
                                        reorderMapping.old_to_new[s.original_index] === partitionBoundary
                                    )?.hit_rate ?? 0 * 100).toFixed(1)}% &lt; 70%)
                                </span>
                            )}
                        </div>

                        {/* 分区可视化 */}
                        <div className="border-2 border-gray-300 inline-block">
                            <div className="flex">
                                <div className="border border-gray-300 p-4 bg-green-50">
                                    <div className="text-center">
                                        <div className="font-semibold">区域①</div>
                                        <div className="text-xs">高×高</div>
                                    </div>
                                </div>
                                <div className="border border-gray-300 p-4 bg-yellow-50">
                                    <div className="text-center">
                                        <div className="font-semibold">区域②</div>
                                        <div className="text-xs">高×低</div>
                                    </div>
                                </div>
                                <div className="ml-2 flex items-center text-xs text-gray-600">
                                    ← 高命中率行 (0-{partitionBoundary - 1})
                                </div>
                            </div>
                            <div className="flex">
                                <div className="border border-gray-300 p-4 bg-yellow-50">
                                    <div className="text-center">
                                        <div className="font-semibold">区域③</div>
                                        <div className="text-xs">低×高</div>
                                    </div>
                                </div>
                                <div className="border border-gray-300 p-4 bg-red-50">
                                    <div className="text-center">
                                        <div className="font-semibold">区域④</div>
                                        <div className="text-xs">低×低</div>
                                    </div>
                                </div>
                                <div className="ml-2 flex items-center text-xs text-gray-600">
                                    ← 低命中率行 ({partitionBoundary}-{n - 1})
                                </div>
                            </div>
                            <div className="flex justify-around text-xs text-gray-600 mt-2 px-4">
                                <div>↑<br />高命中列<br />(0-{partitionBoundary - 1})</div>
                                <div>↑<br />低命中列<br />({partitionBoundary}-{n - 1})</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 步骤4: 四块分区详情 */}
                <div>
                    <h4 className="text-lg font-semibold mb-3">🔍 步骤4: 四块分区详情</h4>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            {/* 区域① */}
                            <div className="border rounded p-4">
                                {renderMiniMatrix(
                                    0,
                                    partitionBoundary,
                                    0,
                                    partitionBoundary,
                                    '区域① (高×高)',
                                    '#f6ffed'
                                )}
                            </div>

                            {/* 区域② */}
                            <div className="border rounded p-4">
                                {renderMiniMatrix(
                                    0,
                                    partitionBoundary,
                                    partitionBoundary,
                                    n,
                                    '区域② (高×低)',
                                    '#fffbe6'
                                )}
                            </div>

                            {/* 区域③ */}
                            <div className="border rounded p-4">
                                {renderMiniMatrix(
                                    partitionBoundary,
                                    n,
                                    0,
                                    partitionBoundary,
                                    '区域③ (低×高)',
                                    '#fffbe6'
                                )}
                            </div>

                            {/* 区域④ */}
                            <div className="border rounded p-4">
                                {renderMiniMatrix(
                                    partitionBoundary,
                                    n,
                                    partitionBoundary,
                                    n,
                                    '区域④ (低×低)',
                                    '#fff1f0'
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Card>
    );
};

export default MatrixReorderProcess;
